import { gql } from '@apollo/client'
import { StoreModel } from './index'
import { graphQL } from '@mortgage-pos/ui/services/http'
import { save } from './thunks/save.thunk'
import incense from '@mortgage-pos/ui/incense'
import {
  AddressType,
  CoBorrowerAddress,
  CreditReportType,
  DisclosureInformation,
  DisclosureType,
  LiabilityType,
  QuestionSetSlug,
  RobinhoodEventType,
} from '@mortgage-pos/types'
import {
  EmailUtil,
  getGraphQLString,
  isNameSuffix,
  isValidDate,
  normalizeName,
  parsePersonFirstLast,
  personNameMatchesBorrower,
  transformStringToDate,
} from '@mortgage-pos/utils'
import { fannieMaeRoundLtv } from '@mortgage-pos/utils'
import { action, Action, thunk, Thunk, thunkOn, ThunkOn } from 'easy-peasy'
import ApplicationClient, {
  DUPLICATE_BORROWER_EMAIL_ERROR_NAME,
} from '@mortgage-pos/ui/services/ApplicationClient'
import { ExperienceType, LeadSourceType } from '@mortgage-pos/types'
import {
  identifyApplication,
  setUserVars,
} from '@mortgage-pos/ui/utils/fullStory'
import {
  attachRecordIds,
  captureAlfalfaLeadSource,
} from '@mortgage-pos/ui/utils/applicationHelpers'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { disclosureQuestionNameMap } from '@mortgage-pos/ui/data/disclosureQuestionNameMap'
import { variation } from '@mortgage-pos/ui/services/featureFlags'

export const SPOUSE_RELATIONSHIP = 'Spouse'
export const SIMPLE_V2_CO_BORROWER_DUPLICATE_EMAIL_ERROR =
  'Use a different email than yours'

export type SimpleV2CoBorrowerForm = {
  coBorrowerFirstName: string
  coBorrowerLastName: string
  coBorrowerEmail: string
  coBorrowerPhone: string
  coBorrowerDateOfBirth: string
  coBorrowerRelationship: string
  doesCoBorrowerHaveSameAddress: 'Yes' | 'No' | ''
  coBorrowerStreet: string
  coBorrowerApt: string
  coBorrowerCity: string
  coBorrowerState: string
  coBorrowerZipCode: string
}

export type SimpleV2CoBorrowerSavePayload = {
  form: SimpleV2CoBorrowerForm
  disclosureText: string
}

// Mirrors the primary's personalDetails consent (authorizationConsent +
// marketingConsent) minus the credit consent, which this copy does not cover.
export const SIMPLE_V2_CO_BORROWER_DISCLOSURE_TYPES = [
  ...disclosureQuestionNameMap.coBorrowerTcpaDisclosure,
  ...disclosureQuestionNameMap.coBorrowerAuthorizationWithoutCreditConsent,
  ...disclosureQuestionNameMap.coBorrowerMarketingConsent,
]

export type SimpleV2CoBorrowerSaveResult =
  | 'saved'
  | 'duplicateEmail'
  | 'failed'
  | 'alreadySaving'

export type CitizenshipAnswers = {
  declarationCitizen: boolean
  declarationResident: boolean
  declarationCitizenStatus: string
}

export type SimpleV2CoBorrowerSsnSavePayload = {
  ssn: string
  citizenship: CitizenshipAnswers
  disclosureType: DisclosureType
  disclosureText: string
}

export type SimpleV2CoBorrowerCreditScoresSavePayload = {
  primaryCreditScore: number
  coBorrowerCreditScore: number
  citizenship: CitizenshipAnswers
}

export type SimpleV2CoBorrowerCreditSaveResult = Exclude<
  SimpleV2CoBorrowerSaveResult,
  'duplicateEmail'
>

export interface ApplicationModel {
  applicationId: number
  applicationGuid: string
  applicationLeadSource: string
  isPhoenixManagedApplication: boolean
  applicationCreatedAt: Date
  coBorrowerId: number
  creditOrdering: boolean
  isSavingSimpleV2CoBorrower: boolean
  setApplicationId: Action<ApplicationModel, number>
  setCreditOrdering: Action<ApplicationModel, boolean>
  setApplicationGuid: Action<ApplicationModel, string>
  setApplicationLeadSource: Action<ApplicationModel, string>
  setIsPhoenixManagedApplication: Action<ApplicationModel, boolean>
  setApplicationCreatedAt: Action<ApplicationModel, Date>
  setCoBorrowerId: Action<ApplicationModel, number>
  setIsSavingSimpleV2CoBorrower: Action<ApplicationModel, boolean>
  saveSimpleV2CoBorrower: Thunk<
    ApplicationModel,
    SimpleV2CoBorrowerSavePayload,
    null,
    StoreModel,
    Promise<SimpleV2CoBorrowerSaveResult>
  >
  saveSimpleV2CoBorrowerSsn: Thunk<
    ApplicationModel,
    SimpleV2CoBorrowerSsnSavePayload,
    null,
    StoreModel,
    Promise<SimpleV2CoBorrowerCreditSaveResult>
  >
  saveSimpleV2CoBorrowerCreditScores: Thunk<
    ApplicationModel,
    SimpleV2CoBorrowerCreditScoresSavePayload,
    null,
    StoreModel,
    Promise<SimpleV2CoBorrowerCreditSaveResult>
  >
  initialize: Thunk<ApplicationModel, null, object, StoreModel>
  hydrateLeadSource: Thunk<ApplicationModel, null, object, StoreModel>
  softPullCredit: Thunk<ApplicationModel, boolean, null, StoreModel>
  coreLogicSoftPullCredit: Thunk<ApplicationModel, null, null, StoreModel>
  hardPullCredit: Thunk<
    ApplicationModel,
    null,
    null,
    StoreModel,
    Promise<boolean>
  >
  orderFannieMaeDirect: Thunk<ApplicationModel, null, null, StoreModel>
  compareCreditScores: Thunk<ApplicationModel, null, null, StoreModel>
  save: Thunk<ApplicationModel, any, null, StoreModel>
  updateStatus: Thunk<
    ApplicationModel,
    string,
    null,
    StoreModel,
    Promise<boolean>
  >
  shareStatusUpdateWithRobinhood: Thunk<
    ApplicationModel,
    RobinhoodEventType,
    null,
    StoreModel
  >
  setExperienceType: Thunk<ApplicationModel, string>
  processLiabilitiesAndCreditScore: Thunk<
    ApplicationModel,
    string,
    null,
    StoreModel
  >
  handleApplicationSession: ThunkOn<ApplicationModel, null, StoreModel>
}

const application = (): ApplicationModel => {
  return {
    applicationId: null,

    applicationGuid: null,

    applicationLeadSource: null,

    isPhoenixManagedApplication: false,

    applicationCreatedAt: null,

    coBorrowerId: null,

    creditOrdering: false,

    isSavingSimpleV2CoBorrower: false,

    setApplicationId: action((state, payload) => {
      setUserVars({ applicationId_int: payload })
      state.applicationId = payload
    }),

    setCreditOrdering: action((state, payload) => {
      state.creditOrdering = payload
    }),

    handleApplicationSession: thunkOn(
      (actions, storeActions) => [
        actions.setApplicationId,
        storeActions.questionnaire.setSlug,
      ],
      async (actions, target, { getState, getStoreState }) => {
        try {
          const { applicationId } = getState()
          const { applicationGuid } = getState()
          const { slug } = getStoreState().questionnaire

          if (applicationId && slug) {
            await ApplicationClient.fillSession(slug)

            if (applicationGuid) {
              // fillSession can create Phoenix management for fresh SIMPLE v2 apps.
              // Re-read it before finish flow selection uses the store flag.
              const response = await getAppIdFromGuid(applicationGuid)
              const appInfo = response?.data?.data?.getApplicationIdFromGuid
              actions.setIsPhoenixManagedApplication(
                Boolean(appInfo?.isPhoenixManagedApplication)
              )
            }
          }
        } catch (e) {
          incense(e)
            .details({
              name: 'ApplicationStore',
              message: 'error during handleApplicationSession thunkOn',
            })
            .sensitive({ target })
            .error()
        }
      }
    ),

    setApplicationGuid: action((state, payload) => {
      identifyApplication(payload)
      setUserVars({ applicationGuid_str: payload })
      state.applicationGuid = payload
    }),

    setApplicationLeadSource: action((state, payload) => {
      state.applicationLeadSource = payload
    }),

    setIsPhoenixManagedApplication: action((state, payload) => {
      state.isPhoenixManagedApplication = payload
    }),

    setApplicationCreatedAt: action((state, payload) => {
      state.applicationCreatedAt = payload
    }),

    setCoBorrowerId: action((state, payload) => {
      setUserVars({ coBorrowerId_int: payload })
      state.coBorrowerId = payload
    }),

    setIsSavingSimpleV2CoBorrower: action((state, payload) => {
      state.isSavingSimpleV2CoBorrower = payload
    }),

    saveSimpleV2CoBorrower: thunk(
      async (
        actions,
        { form, disclosureText },
        { getState, getStoreState, getStoreActions }
      ) => {
        if (getState().isSavingSimpleV2CoBorrower) return 'alreadySaving'
        actions.setIsSavingSimpleV2CoBorrower(true)

        const { applicationId, coBorrowerId } = getState()
        const { mergedAnswers } = getStoreState().answers
        const { slug } = getStoreState().questionnaire
        const { doesCoBorrowerHaveSameAddress } = form
        const coBorrowerPersonalInfo = [
          {
            firstName: form.coBorrowerFirstName.trim(),
            lastName: form.coBorrowerLastName.trim(),
            email: form.coBorrowerEmail.trim(),
            phone: form.coBorrowerPhone,
            dateOfBirth: form.coBorrowerDateOfBirth,
            marriedToPrimary:
              form.coBorrowerRelationship === SPOUSE_RELATIONSHIP,
          },
        ]

        try {
          const savedCoBorrowerId = await ApplicationClient.fillCoBorrower(
            applicationId,
            coBorrowerId,
            { coBorrowerPersonalInfo, doesCoBorrowerHaveSameAddress },
            { throwOnDuplicateEmail: true }
          )
          if (!savedCoBorrowerId) return 'failed'
          actions.setCoBorrowerId(savedCoBorrowerId)

          const disclosures: DisclosureInformation[] =
            SIMPLE_V2_CO_BORROWER_DISCLOSURE_TYPES.map((disclosureType) => ({
              disclosureType,
              disclosureText,
            }))
          const disclosuresSaved =
            await ApplicationClient.fillCoBorrowerDisclosures(disclosures, slug)
          if (!disclosuresSaved) return 'failed'

          const savedAnswers: Record<string, unknown> = {
            hasCoBorrower: 'Yes',
            coBorrowerPersonalInfo,
            doesCoBorrowerHaveSameAddress,
          }

          const coBorrowerResidence =
            doesCoBorrowerHaveSameAddress === 'No'
              ? {
                  street: form.coBorrowerStreet,
                  apt: form.coBorrowerApt,
                  city: form.coBorrowerCity,
                  state: form.coBorrowerState,
                  zipCode: form.coBorrowerZipCode,
                }
              : primaryCurrentResidence(mergedAnswers)

          if (coBorrowerResidence) {
            const coBorrowerAddresses: CoBorrowerAddress[] = [
              {
                type: AddressType.Current,
                ...coBorrowerResidence,
                ...currentCoBorrowerAddressId(mergedAnswers),
              },
            ]
            const addressIds = await ApplicationClient.fillCoBorrowerAddresses(
              applicationId,
              savedCoBorrowerId,
              coBorrowerAddresses
            )
            if (addressIds === null) return 'failed'
            savedAnswers.coBorrowerAddresses = attachRecordIds(
              addressIds,
              coBorrowerAddresses
            )
          }

          getStoreActions().answers.setAnswers(savedAnswers)
          await getStoreActions().questionnaire.next()
          return 'saved'
        } catch (error) {
          if (error?.name === DUPLICATE_BORROWER_EMAIL_ERROR_NAME) {
            return 'duplicateEmail'
          }
          incense(error)
            .details({
              name: 'ApplicationStore',
              message: 'error during saveSimpleV2CoBorrower thunk',
            })
            .safe({ applicationId, coBorrowerId })
            .error()
          return 'failed'
        } finally {
          actions.setIsSavingSimpleV2CoBorrower(false)
        }
      }
    ),

    saveSimpleV2CoBorrowerSsn: thunk(
      async (
        actions,
        { ssn, citizenship, disclosureType, disclosureText },
        { getState, getStoreState, getStoreActions }
      ) => {
        if (getState().isSavingSimpleV2CoBorrower) return 'alreadySaving'

        const { applicationId, coBorrowerId } = getState()
        if (!coBorrowerId) return 'failed'
        actions.setIsSavingSimpleV2CoBorrower(true)

        const { slug } = getStoreState().questionnaire

        try {
          const ssnSaved = await ApplicationClient.saveCoBorrowerSSNAlias(
            applicationId,
            coBorrowerId,
            ssn
          )
          if (!ssnSaved) return 'failed'

          const savedCoBorrowerId = await ApplicationClient.fillCoBorrower(
            applicationId,
            coBorrowerId,
            coBorrowerCitizenshipAnswers(citizenship)
          )
          if (!savedCoBorrowerId) return 'failed'

          const disclosuresSaved =
            await ApplicationClient.fillCoBorrowerDisclosures(
              [{ disclosureType, disclosureText }],
              slug
            )
          if (!disclosuresSaved) return 'failed'

          getStoreActions().answers.setAnswers({ hasSavedCoBorrowerSSN: true })
          await getStoreActions().questionnaire.next()
          return 'saved'
        } catch (error) {
          incense(error)
            .details({
              name: 'ApplicationStore',
              message: 'error during saveSimpleV2CoBorrowerSsn thunk',
            })
            .safe({ applicationId, coBorrowerId })
            .error()
          return 'failed'
        } finally {
          actions.setIsSavingSimpleV2CoBorrower(false)
        }
      }
    ),

    saveSimpleV2CoBorrowerCreditScores: thunk(
      async (
        actions,
        { primaryCreditScore, coBorrowerCreditScore, citizenship },
        { getState, getStoreActions }
      ) => {
        if (getState().isSavingSimpleV2CoBorrower) return 'alreadySaving'

        const { applicationId, coBorrowerId } = getState()
        if (!coBorrowerId) return 'failed'
        actions.setIsSavingSimpleV2CoBorrower(true)

        try {
          const savedCoBorrowerId = await ApplicationClient.fillCoBorrower(
            applicationId,
            coBorrowerId,
            {
              ...coBorrowerCitizenshipAnswers(citizenship),
              coBorrowerCreditScore,
            }
          )
          if (!savedCoBorrowerId) return 'failed'

          await actions.save({ creditScore: primaryCreditScore })

          getStoreActions().answers.setAnswers({
            creditScore: primaryCreditScore,
            coBorrowerCreditScore,
          })
          await getStoreActions().questionnaire.next()
          return 'saved'
        } catch (error) {
          incense(error)
            .details({
              name: 'ApplicationStore',
              message: 'error during saveSimpleV2CoBorrowerCreditScores thunk',
            })
            .safe({ applicationId, coBorrowerId })
            .error()
          return 'failed'
        } finally {
          actions.setIsSavingSimpleV2CoBorrower(false)
        }
      }
    ),

    processLiabilitiesAndCreditScore: thunk(
      async (_, payload, { getState, getStoreActions, getStoreState }) => {
        const enableAlfieProcessLiabilitiesAndCredit = await variation(
          FEATURE_FLAG_TYPES.ENABLE_ALFIE_PROCESS_LIABILITIES_AND_CREDIT
        )

        if (!enableAlfieProcessLiabilitiesAndCredit) {
          // By returning nothing will be done with the data
          // Turing this off in test is a good way to credit/liabilities not being returned
          return
        }

        try {
          const liabilitiesAndCreditScore =
            await ApplicationClient.getLiabilitiesAndCreditScore(
              getState().applicationGuid
            )

          if (!liabilitiesAndCreditScore) {
            return
          }

          const { setAnswers } = getStoreActions().answers
          const { mergedAnswers } = getStoreState().answers

          const updatedAnswers: {
            loanToValue?: number
            debtToIncome?: number
            remainingBalance?: number
            creditScore?: number
          } = {}

          const { liabilities, creditScore } = liabilitiesAndCreditScore

          // Find the highest unpaid balance mortgage
          let highestUnpaidBalanceMortgage = null
          let totalMortgages = 0
          liabilities.forEach((liability) => {
            if (
              !highestUnpaidBalanceMortgage ||
              liability.unpaidBalance >
                highestUnpaidBalanceMortgage.unpaidBalance
            ) {
              highestUnpaidBalanceMortgage = liability
            }

            if (liability.liabilityType === LiabilityType.MortgageLoan) {
              totalMortgages++
            }
          })

          // Calculate LTV if a mortgage was found
          if (highestUnpaidBalanceMortgage) {
            const ltvDecimal =
              highestUnpaidBalanceMortgage.unpaidBalance /
              mergedAnswers.estimatedValue
            updatedAnswers.loanToValue = fannieMaeRoundLtv(ltvDecimal)

            updatedAnswers.remainingBalance =
              highestUnpaidBalanceMortgage.unpaidBalance

            if (totalMortgages === 1) {
              // TODO: set the property use to primary
              // Is this a request to update the address?
            }
          }

          // Calculate DTI
          let totalMonthlyPayments = 0
          liabilities.forEach((liability) => {
            totalMonthlyPayments += liability.monthlyExpense
          })

          if (
            mergedAnswers.grossAnnualIncome &&
            mergedAnswers.grossAnnualIncome > 0
          ) {
            updatedAnswers.debtToIncome =
              (totalMonthlyPayments / (mergedAnswers.grossAnnualIncome / 12)) *
              100
          }

          updatedAnswers.creditScore = creditScore || 0

          setAnswers(updatedAnswers)
        } catch (e) {
          incense(e)
            .details({
              name: 'ApplicationStore',
              message: 'error during processLiabilitiesAndCreditScore thunk',
            })
            .safe({ applicationGuid: getState().applicationGuid })
            .error()
        }
      }
    ),

    initialize: thunk(
      async (
        {
          setApplicationId,
          setApplicationGuid,
          setApplicationLeadSource,
          setIsPhoenixManagedApplication,
        },
        _,
        { getStoreState }
      ) => {
        const {
          bankrateLeadId,
          fBLeadId,
          referrerUrl,
          landingUrl,
          isPhoneFlow,
          isAmazonBenefitsFlow,
          directMailerOfferCode,
          isRobinhoodGoldFlow,
          isRobinhoodConcierge,
          slug,
        } = getStoreState().questionnaire
        const { isLeComparisonApp } = getStoreState().leComparison

        const urlParams = new URLSearchParams(window.location.search)
        const loValue = urlParams.get('loan-officer-id')
        const loanOfficerId = loValue ? Number(loValue) : null

        const phoneAppGuid = urlParams.get('application-guid')
        const isPhoneFlowEnabled = await variation(
          FEATURE_FLAG_TYPES.PHONE_FLOW_ENABLED
        )

        // Phone flow requires the PHONE_FLOW_ENABLED flag. Non-phone flows that
        // arrive with an application-guid in the URL (e.g. the borrower link
        // redirect from q-redirect) should always load the existing application
        // rather than creating a new one.
        if (phoneAppGuid && (!isPhoneFlow || isPhoneFlowEnabled)) {
          const response = await getAppIdFromGuid(phoneAppGuid)
          const appInfo = response?.data?.data?.getApplicationIdFromGuid
          setApplicationId(appInfo?.applicationId)
          setApplicationLeadSource(appInfo?.applicationLeadSource)
          setIsPhoenixManagedApplication(
            Boolean(appInfo?.isPhoenixManagedApplication)
          )
          return
        }

        const { applicationId, applicationGuid, applicationLeadSource } =
          await ApplicationClient.initialize(
            bankrateLeadId,
            loanOfficerId,
            fBLeadId,
            getLeadSource(
              isLeComparisonApp,
              directMailerOfferCode,
              isAmazonBenefitsFlow,
              isRobinhoodGoldFlow,
              isRobinhoodConcierge,
              slug
            ),
            landingUrl,
            directMailerOfferCode,
            referrerUrl
          )

        setApplicationGuid(applicationGuid)
        setApplicationLeadSource(applicationLeadSource)
        setIsPhoenixManagedApplication(false)
        setApplicationId(applicationId)
      }
    ),

    // Resuming through application-guid hydrates the id from the answers query
    // and skips initialize, leaving the lead source unset for the session.
    hydrateLeadSource: thunk(
      async ({ setApplicationLeadSource }, _, { getState }) => {
        const { applicationGuid, applicationLeadSource } = getState()
        if (!applicationGuid || applicationLeadSource) return

        try {
          const response = await getAppIdFromGuid(applicationGuid)
          const application = response?.data?.data?.getApplicationIdFromGuid
          if (response?.data?.errors?.length || !application) {
            throw new Error('Failed to load the resumed application source')
          }
          const leadSource = application.applicationLeadSource

          if (leadSource) {
            setApplicationLeadSource(leadSource)
          }
        } catch (error) {
          incense(error)
            .details({
              name: 'ApplicationStore',
              message: 'error during hydrateLeadSource thunk',
            })
            .safe({ applicationGuid })
            .error()
        }
      }
    ),

    softPullCredit: thunk(
      async (
        _,
        isTrimerge = false,
        { getState, getStoreActions, getStoreState }
      ) => {
        const { applicationId, applicationGuid } = getState()
        const { isSplitFlow } = getStoreState().questionnaire
        const { setMortgageRateEstimate } = getStoreActions().bankrateCompare
        const { processLiabilitiesAndCreditScore } =
          getStoreActions().application

        const { setHasRunCreditPull, setWasCreditPullSuccessful } =
          getStoreActions().leComparison

        const { answer } = getStoreActions().questionnaire
        const { trackCreditPullInGtm } = getStoreActions().tracking

        // For compare flow
        setHasRunCreditPull(true)

        let success = false

        try {
          // Normal path - make the actual API call
          const res = await ApplicationClient.softPullCreditScore(
            applicationId,
            !!isSplitFlow,
            isTrimerge
          )

          // Capture pull outcome immediately — downstream work must not influence whether the pull itself is reported as successful
          success = !!(res as any)?.success

          // Needed for compare flow to determine if we should use a fallback score
          console.log('AlfieBUG [Store][SoftPull] normal path response', {
            applicationId,
            hasResp: !!res,
            success,
          })

          setWasCreditPullSuccessful(success)

          if (res?.mortgageRateEstimate) {
            const { openedDate, paymentScheduleMonthCount, currentBalance } =
              res.mortgageRateEstimate

            setMortgageRateEstimate(res.mortgageRateEstimate)

            await answer({
              currentLoanStartDate: openedDate,
              currentLoanTerm: paymentScheduleMonthCount / 12,
              remainingBalance: currentBalance,
            })
          }

          await processLiabilitiesAndCreditScore(applicationGuid)
        } finally {
          trackCreditPullInGtm({ success })
        }

        return success
      }
    ),

    // Currently not in use
    coreLogicSoftPullCredit: thunk(async (_, __, { getState }) => {
      const { applicationId } = getState()
      await ApplicationClient.pullCredit(
        applicationId,
        CreditReportType.Prequal
      )
    }),

    hardPullCredit: thunk(async (_, __, { getState }) => {
      const { applicationId } = getState()
      return await ApplicationClient.pullCredit(
        applicationId,
        CreditReportType.Trimerge
      )
    }),

    orderFannieMaeDirect: thunk((_, __, ___) => {
      return ApplicationClient.orderFannieMaeDirect()
    }),

    compareCreditScores: thunk(async (_, __, { getState }) => {
      const { applicationId } = getState()
      return await ApplicationClient.compareCreditScores(applicationId)
    }),

    save: save(),

    updateStatus: thunk(async (_, status, { getState }) => {
      const { applicationId } = getState()
      return ApplicationClient.updateQStatus(applicationId, status)
    }),

    setExperienceType: thunk(
      async (_, experienceType: ExperienceType, { getState }) => {
        const { applicationId } = getState()

        try {
          await graphQL({
            query: setExperienceTypeMutation,
            variables: {
              applicationId: null,
              experienceType: experienceType as string,
            },
          })
        } catch (error) {
          incense(error)
            .details({
              name: 'SetExperienceTypeError',
              message: 'Failed to set experience type on application',
            })
            .safe({ applicationId })
            .error()
        }
      }
    ),

    shareStatusUpdateWithRobinhood: thunk(async (_, eventType) => {
      try {
        await ApplicationClient.shareStatusUpdateWithRobinhood(eventType)
      } catch (error) {
        incense(error)
          .details({
            name: 'ShareStatusUpdateWithRobinhoodError',
            message: 'Failed to share status update with Robinhood',
          })
          .safe({ eventType })
          .error()
      }
    }),
  }
}

// TODO: remove applicationId from query/mutation
export const setExperienceTypeMutation = getGraphQLString(gql`
  mutation setExperienceType(
    $applicationId: Int
    $experienceType: ExperienceType!
  ) {
    setExperienceType(
      applicationId: $applicationId
      experienceType: $experienceType
    )
  }
`)

function getLeadSource(
  isLeComparisonApp,
  directMailerOfferCode,
  isAmazonBenefitsDemo,
  isRobinhoodGoldFlow = false,
  isRobinhoodConcierge = false,
  slug = ''
) {
  let leadSource = null

  if (isRobinhoodConcierge || slug === QuestionSetSlug.RhConcierge) {
    leadSource = LeadSourceType.RobinhoodConcierge
  } else if (isRobinhoodGoldFlow) {
    leadSource = LeadSourceType.Robinhood
  } else if (isLeComparisonApp) {
    leadSource = LeadSourceType.LoanEstimateComparison
  } else if (directMailerOfferCode) {
    leadSource = LeadSourceType.Transunion
  }

  if (isAmazonBenefitsDemo) {
    leadSource = LeadSourceType.AmazonBenefitsDemo
  }

  return leadSource || captureAlfalfaLeadSource()
}

export default application

function currentCoBorrowerAddressId(mergedAnswers): { id?: number } {
  const id = mergedAnswers?.coBorrowerAddresses?.find(
    (address) => address?.type === AddressType.Current
  )?.id
  return id ? { id } : {}
}

function coBorrowerCitizenshipAnswers({
  declarationCitizen,
  declarationResident,
  declarationCitizenStatus,
}: CitizenshipAnswers) {
  return {
    coBorrowerDeclarationCitizen: declarationCitizen,
    coBorrowerDeclarationResident: declarationResident,
    coBorrowerPersonalInfo: [{ declarationCitizenStatus }],
  }
}

function primaryCurrentResidence(
  mergedAnswers
): Omit<CoBorrowerAddress, 'type'> | null {
  const residence = [mergedAnswers?.['addresses+0'], mergedAnswers?.addresses]
    .filter(Array.isArray)
    .flat()
    .find((address) => address?.type === AddressType.Current && address.street)
  if (!residence) return null

  const { street, apt, city, state, zipCode } = residence
  return { street, apt, city, state, zipCode }
}

function toYesNo(value: unknown): 'Yes' | 'No' | '' {
  if (value === true || value === 'Yes') return 'Yes'
  if (value === false || value === 'No') return 'No'
  return ''
}

const JOINT_OWNER_NAME = /[&/]|\b(AND|OR|POD|ITF)\b/i

function detectedCoBorrowerName(mergedAnswers) {
  const names = mergedAnswers?.secondAccountOwnerNames
  if (!Array.isArray(names) || names.length !== 1) return null
  const ownerName = typeof names[0] === 'string' ? names[0].trim() : ''
  const tokens = ownerName.split(/\s+/)
  if (
    JOINT_OWNER_NAME.test(ownerName) ||
    tokens.length < 2 ||
    !parsePersonFirstLast(ownerName)
  ) {
    return null
  }
  const primary = {
    firstName:
      mergedAnswers?.firstName ?? mergedAnswers?.personalInfo?.[0]?.firstName,
    lastName:
      mergedAnswers?.lastName ?? mergedAnswers?.personalInfo?.[0]?.lastName,
  }
  if (personNameMatchesBorrower(ownerName, primary)) return null

  let lastNameIndex = tokens.length - 1
  while (lastNameIndex > 1 && isNameSuffix(tokens[lastNameIndex])) {
    lastNameIndex--
  }

  return {
    firstName: normalizeName(tokens[0].toLowerCase()),
    lastName: normalizeName(tokens[lastNameIndex].toLowerCase()),
  }
}

export function getSimpleV2CoBorrowerFormValues(
  mergedAnswers,
  coBorrowerId: number | null
): SimpleV2CoBorrowerForm {
  const personalInfo = mergedAnswers?.coBorrowerPersonalInfo?.[0] ?? {}
  const address = coBorrowerId
    ? (mergedAnswers?.coBorrowerAddresses?.find(
        (candidate) => candidate?.type === AddressType.Current
      ) ?? {})
    : {}
  const detectedName = coBorrowerId
    ? null
    : detectedCoBorrowerName(mergedAnswers)

  return {
    coBorrowerFirstName:
      personalInfo.firstName || detectedName?.firstName || '',
    coBorrowerLastName: personalInfo.lastName || detectedName?.lastName || '',
    coBorrowerEmail: personalInfo.email || '',
    coBorrowerPhone: personalInfo.phone || '',
    coBorrowerDateOfBirth: personalInfo.dateOfBirth || '',
    coBorrowerRelationship:
      toYesNo(personalInfo.marriedToPrimary) === 'Yes'
        ? SPOUSE_RELATIONSHIP
        : '',
    doesCoBorrowerHaveSameAddress: coBorrowerId
      ? toYesNo(mergedAnswers?.doesCoBorrowerHaveSameAddress)
      : '',
    coBorrowerStreet: address.street || '',
    coBorrowerApt: address.apt || '',
    coBorrowerCity: address.city || '',
    coBorrowerState: address.state || '',
    coBorrowerZipCode: address.zipCode || '',
  }
}

const normalizeEmail = (email?: string) => email?.trim().toLowerCase() || ''

function isValidPastDate(value: string): boolean {
  const date = transformStringToDate(value)
  return isValidDate(date) && date < new Date() && date.getFullYear() >= 1900
}

// Address fields validate themselves in SimpleV2AddressFields.
export function validateSimpleV2CoBorrowerForm(
  form: SimpleV2CoBorrowerForm,
  primaryEmail?: string
): Partial<Record<keyof SimpleV2CoBorrowerForm, string>> {
  const errors: Partial<Record<keyof SimpleV2CoBorrowerForm, string>> = {}
  const email = form.coBorrowerEmail?.trim()

  if (!form.coBorrowerFirstName?.trim()) {
    errors.coBorrowerFirstName = 'First name is required'
  }
  if (!form.coBorrowerLastName?.trim()) {
    errors.coBorrowerLastName = 'Last name is required'
  }
  if (!email) {
    errors.coBorrowerEmail = 'Email is required'
  } else if (!EmailUtil.checkEmailValidity(email)) {
    errors.coBorrowerEmail = 'Enter a valid email'
  } else if (normalizeEmail(email) === normalizeEmail(primaryEmail)) {
    errors.coBorrowerEmail = SIMPLE_V2_CO_BORROWER_DUPLICATE_EMAIL_ERROR
  }
  if (!/^\d{10}$/.test(form.coBorrowerPhone || '')) {
    errors.coBorrowerPhone = 'Enter a 10-digit phone number'
  }
  if (!isValidPastDate(form.coBorrowerDateOfBirth)) {
    errors.coBorrowerDateOfBirth = 'Enter a valid date of birth'
  }
  if (!form.coBorrowerRelationship) {
    errors.coBorrowerRelationship = 'Select a relationship'
  }
  if (!form.doesCoBorrowerHaveSameAddress) {
    errors.doesCoBorrowerHaveSameAddress = 'Select Yes or No'
  }

  return errors
}

async function getAppIdFromGuid(applicationGuid: string) {
  return await graphQL({
    query: getAppIdFromGuidQuery,
    variables: {
      applicationGuid,
    },
  })
}
export const getAppIdFromGuidQuery = getGraphQLString(gql`
  query getApplicationIdFromGuid($applicationGuid: String!) {
    getApplicationIdFromGuid(applicationGuid: $applicationGuid) {
      applicationId
      applicationLeadSource
      isPhoenixManagedApplication
    }
  }
`)
